import {
  ClickAwayListener,
  Collapse,
  Grow,
  InputAdornment,
  InputBaseComponentProps,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  TextField,
} from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ExternalLink } from "ExternalLink";
import { OLButton } from "OLButton";
import { Search } from "components/FormControls/Select/Search";
import { inputStylesMultiLine } from "components/FormControls/styles";
import { ReactComponent as ChevronDownIcon } from "components/Icons/chevron-down.svg";
import { ReactComponent as ChevronUpIcon } from "components/Icons/chevron-up.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { Field, getIn } from "formik";
import { useMessage } from "hooks/intl";
import { useDebounce } from "hooks/useDebounce";
import { kebabCase } from "lodash";
import React, { forwardRef, useRef, useState } from "react";
import { PickRequired } from "types/global";
import { EffectDateTimeDialog } from "./EffectDateTimeDialog";
import { Dialog, EffectDialog } from "./EffectDialog";
import { EffectInput } from "./EffectInput";
import { EffectItemsCriteriaDialog } from "./EffectItemsCriteriaDialog";
import EffectWalletDialog from "./EffectWalletDialog";
import { filterElements } from "./utils";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    inputBase: {
      background: theme.white50,
    },
    input: {
      width: "100%",
      ...inputStylesMultiLine,
      "& .MuiOutlinedInput-input": {
        padding: "0 6px",
      },
    },
    wrapper: {
      position: "relative",
      display: "flex",
      alignItems: "flex-start",
      flexDirection: "column",
    },
    selected: {
      backgroundColor: "#5c4baa14",
    },
    item: {
      display: "flex",
      gap: theme.spacing(4),
      borderRadius: "8px",
      margin: theme.spacing(0, 4),
      padding: theme.spacing(4),
      whiteSpace: "normal",
    },
    nestedItem: {
      borderRadius: "8px",
      margin: theme.spacing(0, 4),
      padding: theme.spacing(4, 11.5),
    },
    list: {
      paddingTop: 0,
    },
    itemsWrapper: {
      maxHeight: "240px",
      overflowY: "auto",
      padding: 0,
      margin: 0,
    },
    noOption: {
      color: theme.gray400,
    },
    helperText: {
      background: "transparent",
    },
    menu: {
      zIndex: 1000,
      boxShadow:
        "0px 1px 18px 0px rgba(0, 0, 0, 0.12), 0px 6px 11px 0px rgba(0, 0, 0, 0.14), 0px 3px 7px -1px rgba(0, 0, 0, 0.20)",
    },
    rule: {
      display: "flex",
      flexDirection: "column",
    },
    ruleCode: {
      fontSize: "12px",
      color: theme.gray400,
    },
    btnWrapper: {
      display: "flex",
      gap: theme.spacing(5),
      padding: theme.spacing(8),
    },
    dropdownIcon: {
      cursor: "pointer",
      minHeight: "100%",
      position: "absolute",
      top: 0,
      right: 0,
      padding: theme.spacing(0, 8),
    },

    autocompleteHint: {
      color: theme.gray500,
      fontSize: "12px",
      marginTop: theme.spacing(1.5),
      overflowX: "auto",
    },
    bolded: {
      fontWeight: 700,
    },
  }),
);

export interface DataType {
  name: string;
  items: DataItemType[];
}

export interface DataItemType {
  label: string;
  code?: string;
  parameter?: string;
  parameterType?: "datetime" | "text" | "itemsCriteria" | "wallet";
  codeRegexMatcher?: RegExp;
  dialogTitle?: string;
  // dialogDescription?: React.ReactElement | string;
  dialogDescription?: string;
  onSelect?: () => DataItemType;
  hideCode?: boolean;
  attributes?: PickRequired<DataItemType, "code" | "codeRegexMatcher">[];
}

export interface SelectOption {
  id: string;
  label: string;
  category?: string;
}

interface Props {
  "data-test-id"?: string;
  // keep max menu width same as dropdown
  adjustListWidthToParent?: boolean;
  className?: string;
  disabled?: boolean;
  helperText?: string;
  items: Array<DataType>;
  multiInput?: boolean;
  name: string;
  onItemSelect?: (item: DataItemType) => void;
  placeholder?: string;
  showHint?: boolean;
}

export function EffectSearch({
  "data-test-id": dataTestId,
  adjustListWidthToParent = true,
  className,
  helperText,
  items = [],
  multiInput = true,
  name,
  onItemSelect,
  placeholder,
  showHint = true,
}: Props) {
  const [category, setCategory] = useState<string | undefined>(items[0]?.name);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [dateTimeDialog, setDateTimeDialog] = useState<Dialog | null>(null);
  const [itemsCriteriaDialog, setItemsCriteriaDialog] = useState<Dialog | null>(
    null,
  );
  const [walletDialog, setWalletDialog] = useState<Dialog | null>(null);
  const [openAttributes, setOpenAttributes] = useState<Record<string, boolean>>(
    {},
  );
  const [searchedValue, setSearchedValue] = useState("");
  const [menuWidth, setMenuWidth] = useState("100%");
  const [isOpen, setOpen] = useState(false);
  const classes = useStyles();
  const { debouncedValue: searchQuery, isTyping } = useDebounce(searchedValue);
  const { message } = useMessage();
  const anchorRef = useRef<HTMLInputElement>(null);

  const itemsFromCategory = category
    ? items.find(({ name }) => name === category)?.items || []
    : [];
  const filteredItems = filterElements(searchQuery, itemsFromCategory);
  const isAnyOption = filteredItems.length > 0;
  const showCategories = items.length > 1;

  const handleToggleAttributes = (key: string) => {
    setOpenAttributes((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className={`${classes.input} ${className}`}>
      <Field name={name}>
        {({ field, form, meta }: { field: any; form: any; meta: any }) => {
          const isError = meta.error || getIn(form.status, field.name);

          return (
            <div className={classes.wrapper}>
              <EffectDialog
                isDialogOpen={!!dialog}
                closeDialog={() => setDialog(null)}
                dialog={dialog}
                setFieldValue={(value) => form.setFieldValue(field.name, value)}
                currentValue={field.value || ""}
                multiInput={multiInput}
              />
              <EffectDateTimeDialog
                dialog={dateTimeDialog}
                isDialogOpen={!!dateTimeDialog}
                closeDialog={() => setDateTimeDialog(null)}
                setFieldValue={(value) => form.setFieldValue(field.name, value)}
              />
              <EffectItemsCriteriaDialog
                isDialogOpen={!!itemsCriteriaDialog}
                closeDialog={() => setItemsCriteriaDialog(null)}
                dialog={itemsCriteriaDialog}
                setFieldValue={(value) => form.setFieldValue(field.name, value)}
                currentValue={field.value || ""}
              />
              <EffectWalletDialog
                isDialogOpen={!!walletDialog}
                dialog={walletDialog}
                closeDialog={() => setWalletDialog(null)}
                setFieldValue={(value) => form.setFieldValue(field.name, value)}
                currentValue={field.value || ""}
                multiInput={multiInput}
              />
              <TextField
                ref={anchorRef}
                className={classes.input}
                placeholder={placeholder}
                InputLabelProps={{
                  shrink: isOpen || !!field.value,
                }}
                inputProps={{
                  readOnly: false,
                  autoComplete: "off",
                  "data-test-id": `effect-search-select`,
                }}
                value={field.value}
                sx={{
                  input: {
                    color: "transparent",
                  },
                }}
                fullWidth
                helperText={isError ? isError : helperText}
                error={isError}
                InputProps={{
                  className: classes.inputBase,
                  inputComponent: forwardRef<
                    HTMLInputElement,
                    InputBaseComponentProps
                  >((props, ref) => (
                    <EffectInput
                      {...props}
                      ref={ref}
                      setFieldValue={(value: string[]) =>
                        form.setFieldValue(field.name, value)
                      }
                      items={items}
                      setDialog={setDialog}
                      setOpen={setOpen}
                    />
                  )),
                  endAdornment: (
                    <InputAdornment
                      className={classes.dropdownIcon}
                      onClick={(event) => {
                        setOpen(!isOpen);
                        adjustListWidthToParent &&
                          setMenuWidth(event.currentTarget.offsetWidth + "px");
                      }}
                      position="end"
                      data-test-id="effect-search-dropdown-icon"
                    >
                      <ChevronDownIcon />
                    </InputAdornment>
                  ),
                }}
                FormHelperTextProps={{
                  className: classes.helperText,
                }}
                data-test-id={dataTestId || "effect-search-select-wrapper"}
              />
              <Popper
                open={isOpen}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                transition
                disablePortal
                sx={{
                  zIndex: 1302,
                }}
                popperOptions={{
                  modifiers: [
                    {
                      name: "flip",
                      options: {
                        fallbackPlacements: [],
                      },
                    },
                  ],
                }}
              >
                {({ TransitionProps }) => (
                  <Grow {...TransitionProps}>
                    <Paper>
                      <ClickAwayListener
                        onClickAway={(event) => {
                          if (
                            (event.target as HTMLElement)?.dataset.role ===
                              "chips" ||
                            (event.target as HTMLElement)?.dataset.role ===
                              "close"
                          )
                            return;
                          setSearchedValue("");
                          setOpen(false);
                          setOpenAttributes({});
                        }}
                      >
                        <MenuList
                          sx={{
                            minWidth: anchorRef?.current?.offsetWidth || "100%",
                            maxWidth: menuWidth,
                            zIndex: 1302,
                          }}
                          className={`${classes.menu} ${classes.list}`}
                        >
                          {showCategories && (
                            <div className={classes.btnWrapper}>
                              {items.map(({ name }, index) => {
                                const isSelected = category === name;
                                return (
                                  <OLButton
                                    key={index}
                                    variant={isSelected ? "contained" : "text"}
                                    onClick={() => {
                                      setSearchedValue("");
                                      setCategory(name);
                                    }}
                                    data-test-id={`effect-search-menu-item-${name}`}
                                  >
                                    {message({ id: name })}
                                  </OLButton>
                                );
                              })}
                            </div>
                          )}
                          <div>
                            <Search
                              searchedValue={searchedValue}
                              setSearchedValue={setSearchedValue}
                              isLoading={isTyping}
                              data-test-id="effect-search"
                            />

                            <ul
                              className={classes.itemsWrapper}
                              data-test-id="effect-search-select-list"
                            >
                              {isAnyOption ? (
                                filteredItems.map(
                                  (item: DataItemType, index: number) => {
                                    const isItemOpen =
                                      !!openAttributes[item.label];

                                    return (
                                      <>
                                        <MenuItem
                                          tabIndex={index}
                                          className={`${classes.item} ${
                                            false ? classes.selected : ""
                                          }`}
                                          key={item.label}
                                          onClick={() => {
                                            if (onItemSelect && item.onSelect) {
                                              return onItemSelect(
                                                item.onSelect(),
                                              );
                                            }

                                            if (
                                              !item.attributes &&
                                              item.parameter &&
                                              item.parameterType
                                            ) {
                                              setOpen(false);

                                              const dialogOpts = {
                                                title: message({
                                                  id:
                                                    item.dialogTitle ||
                                                    item.label,
                                                }),
                                                description: message(
                                                  {
                                                    id: item.dialogDescription,
                                                  },
                                                  {
                                                    span: (
                                                      chunks: React.ReactNode[],
                                                    ) => (
                                                      <span
                                                        className={
                                                          classes.bolded
                                                        }
                                                      >
                                                        {chunks}
                                                      </span>
                                                    ),
                                                  },
                                                ),
                                                code: item.code as string,
                                                parameter: item.parameter,
                                              };

                                              switch (item.parameterType) {
                                                case "text":
                                                  return setDialog(dialogOpts);
                                                case "datetime":
                                                  return setDateTimeDialog(
                                                    dialogOpts,
                                                  );
                                                case "itemsCriteria":
                                                  return setItemsCriteriaDialog(
                                                    dialogOpts,
                                                  );
                                                default:
                                                  return null;
                                              }
                                            }

                                            if (item.attributes) {
                                              return handleToggleAttributes(
                                                item.label,
                                              );
                                            }

                                            const shouldBlockExpressionSpecialQuotes =
                                              item.code === "100";

                                            const newExp =
                                              shouldBlockExpressionSpecialQuotes
                                                ? item.code
                                                : `<#>${item.code}</#>`;

                                            if (multiInput) {
                                              form.setFieldValue(
                                                field.name,
                                                `${field.value || ""} ${newExp}`,
                                              );
                                            } else {
                                              form.setFieldValue(
                                                field.name,
                                                newExp,
                                              );
                                            }
                                          }}
                                          data-test-id={`effect-search-select-list-item-${kebabCase(
                                            item.label,
                                          )}`}
                                        >
                                          <>
                                            <div className={classes.rule}>
                                              <span>
                                                {message({ id: item.label })}
                                              </span>
                                              {!item.hideCode && (
                                                <span
                                                  className={classes.ruleCode}
                                                >
                                                  {item.code}
                                                </span>
                                              )}
                                            </div>
                                            {item.attributes ? (
                                              isItemOpen ? (
                                                <ChevronUpIcon />
                                              ) : (
                                                <ChevronDownIcon />
                                              )
                                            ) : null}
                                          </>
                                        </MenuItem>
                                        {item.attributes ? (
                                          <Collapse in={isItemOpen}>
                                            {item.attributes.map(
                                              (attribute, index) => (
                                                <MenuItem
                                                  tabIndex={index}
                                                  className={classes.nestedItem}
                                                  key={attribute.code}
                                                  onClick={() => {
                                                    setOpen(false);

                                                    const dialogOpts = {
                                                      title: message({
                                                        id: attribute.dialogTitle as string,
                                                      }),
                                                      code: attribute.code,
                                                      parameter:
                                                        attribute.parameter,
                                                    };

                                                    setWalletDialog(dialogOpts);
                                                    setOpenAttributes({});
                                                  }}
                                                  data-test-id={`effect-search-select-list-item-${kebabCase(
                                                    attribute.label,
                                                  )}`}
                                                >
                                                  <div className={classes.rule}>
                                                    <span>
                                                      {message({
                                                        id: attribute.label,
                                                      })}
                                                    </span>
                                                    <span
                                                      className={
                                                        classes.ruleCode
                                                      }
                                                    >
                                                      {attribute.code}
                                                    </span>
                                                  </div>
                                                </MenuItem>
                                              ),
                                            )}
                                          </Collapse>
                                        ) : null}
                                      </>
                                    );
                                  },
                                )
                              ) : (
                                <MenuItem className={classes.noOption}>
                                  {message({ id: "noOptions" })}
                                </MenuItem>
                              )}
                            </ul>
                          </div>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
              {showHint && (
                <div className={classes.autocompleteHint}>
                  {message(
                    { id: "autocompleteLinksText" },
                    {
                      listOfOperators: (
                        <ExternalLink path="https://help.openloyalty.io/main-features/expressions#operators">
                          {message({
                            id: "listOfOperators",
                          }).toLowerCase()}
                        </ExternalLink>
                      ),
                      availableVariables: (
                        <ExternalLink path="https://help.openloyalty.io/main-features/campaigns/creating-campaigns#autocomplete">
                          {message({
                            id: "availableVariablesLinkLabel",
                          })}
                        </ExternalLink>
                      ),
                      exampleFormulas: (
                        <ExternalLink path="https://help.openloyalty.io/expressions">
                          {message({
                            id: "exampleCalculationFormulasLinkLabel",
                          })}
                        </ExternalLink>
                      ),
                    },
                  )}
                </div>
              )}
            </div>
          );
        }}
      </Field>
    </div>
  );
}
